// A team's games beyond the score (/team-match-stats; teams and matches, Ben 2026-10-30): each
// side's xG and shots, the goals and red cards at their minute. For the Matches module showing a
// team (modules/TeamMatches.tsx).

import type { TeamMatchMoment, TeamMatchStats, TeamMatchStatsResponse } from "@catbento/shared";
import type { LiveApi } from "./api.js";
import { rememberFamiliarName } from "../data/familiar-names.js";
import { getPlayerInitialName, getPlayerShortName } from "../data/player-utils.js";
import { clockMinute } from "../data/match-minute.js";

export interface TeamMatchDetail {
  /** By match id; a game without event data isn't here. */
  stats: Map<number, TeamMatchStats>;
  /** By match id, in the order they happened. */
  moments: Map<number, TeamMatchMoment[]>;
}

export async function loadTeamMatchDetail(api: LiveApi, teamId: number, competitionId: number, seasonCode: string): Promise<TeamMatchDetail> {
  const res = await api.get<TeamMatchStatsResponse>(`/team-match-stats?teamId=${teamId}&competitionId=${competitionId}&seasonId=${seasonCode}`);
  return toTeamMatchDetail(res);
}

/** Pure — the answer, keyed by match. */
export function toTeamMatchDetail(res: TeamMatchStatsResponse): TeamMatchDetail {
  const stats = new Map(res.matches.map((m) => [m.matchId, m]));
  const moments = new Map<number, TeamMatchMoment[]>();
  for (const m of res.moments) {
    // The scorers' familiar names, as the site learns them everywhere else.
    rememberFamiliarName(m.playerName, m.playerNickname);
    const list = moments.get(m.matchId);
    if (list) list.push(m);
    else moments.set(m.matchId, [m]);
  }
  return { stats, moments };
}

/** The name a moment shows: the player's familiar name ("Evanilson"), else first + last, as
 *  everywhere on the site. */
export function momentPlayer(m: TeamMatchMoment): string {
  return m.playerName ? getPlayerShortName({ name: m.playerName }) : "Unknown";
}

/** The Match card's shorter name (Ben, 2026-10-31): "Cole Palmer" → "C. Palmer". A familiar
 *  name keeps its surname words ("K. De Bruyne"); one word stays whole ("Rodri"). */
export function momentInitialName(m: TeamMatchMoment): string {
  return m.playerName ? getPlayerInitialName({ name: m.playerName }).replace(/^([^.\s])\./, "$1. ") : "Unknown";
}

/** "xG 1.84 – 0.92" (the team's first). */
export function xgLine(s: TeamMatchStats): string {
  return `xG ${s.for.xg.toFixed(2)} – ${s.against.xg.toFixed(2)}`;
}

/** A moment's tooltip: "Goal · B. Saka 23' (pen)", "Red card · D. Rice 44'" — the opponent's
 *  marked "(opponent)". */
export function momentTooltip(m: TeamMatchMoment): string {
  const what = m.kind === "goal" ? "Goal" : m.kind === "own-goal" ? "Own goal" : "Red card";
  const minute = m.minute === null ? "" : ` ${clockMinute(m.minute, m.period)}`;
  const pen = m.isPenalty ? " (pen)" : "";
  const side = m.forTeam ? "" : " (opponent)";
  return `${what}${side} · ${momentPlayer(m)}${minute}${pen}`;
}
